import React, { useMemo, useEffect } from 'react'

import { noop } from 'lodash'
import {
  NavigationControl,
  MapProps,
  Fog,
  Terrain,
  Map as ReactMapGl,
} from 'react-map-gl'
import { palette } from '@centivo/react-components'
import { createGlobalStyle } from 'styled-components'

import { getAppConfig } from 'src/context/functions/getAppConfig'
import { useSliceTemplate } from 'src/context/global'

import { getBounds } from './functions'
import { Pin } from './Pin'
import { Popup } from './Popup'
import { PinData } from './types'
import { PopupSlice } from './PopupSlice'

const { REACT_APP_MAPBOX_ACCESS_TOKEN } = getAppConfig()

export interface Props
  extends Omit<
    MapProps,
    'mapboxAcessToken' | 'mapStyle' | 'projection' | 'logoPosition'
  > {
  pins?: PinData[]
  selectedPin?: string | null
  onSelectPin?: (pin: string) => void
  fog?: Fog
  terrain?: Terrain
}

export const MapGlobalStyles = createGlobalStyle`
  .mapboxgl-marker {
    > button {
      background-color: transparent;
      border: none;
      padding: 0;
      margin: 0;
      cursor: pointer;

      > img {
        width: 2.5rem;
        height: 2.5rem;
      }
    }
  }

  .mapboxgl-popup {
    &.hidden {
      opacity: 0;
    }

    h6 {
      color: #7a7e81;
      font-weight: 700;
      margin-right: 0.5rem;
    }
  }

  .mapboxgl-popup-content {
    ${({ theme }) => {
      switch (theme.template) {
        case 'template-4':
          return {
            padding: '12px 10px 12px 10px !important',
            borderRadius: '10px',
            border: `1px solid ${palette.brand.lightGray}`,
          }
        default:
          return {
            padding: '0.25rem 1rem 0.25rem 0.5rem',
          }
      }
    }}
  }

  .mapboxgl-popup-anchor-bottom .mapboxgl-popup-tip {
    ${({ theme }) => {
      switch (theme.template) {
        case 'template-4':
          return {
            border: 'none',
          }
        default:
          return {}
      }
    }}
  }

  .mapboxgl-popup-close-button {
    outline: none;
    right: 0.5rem;
    top: 0.5rem;
    color: #7a7e81;
    font-size: 1rem;
    font-weight: 700;

    &:hover {
      background-color: transparent;
    }
  }
`

const style = {
  width: 450,
  height: 480,
  maxWidth: '100%',
  maxHeight: '100%',
  marginBottom: 32,
}

export const Map: React.FC<Props> = ({
  selectedPin,
  pins = [],
  onSelectPin = noop,
  ...props
}) => {
  const isSliceTemplate = useSliceTemplate()

  const initialViewState = getBounds(
    pins.map(({ latitude, longitude }) => ({
      lat: latitude,
      long: longitude,
    }))
  )
  const pinArr = useMemo(
    () =>
      pins.map(pin => (
        <Pin {...pin} onSelect={onSelectPin} key={pin.id} aria-hidden />
      )),
    [onSelectPin, pins]
  )
  const popupArr = useMemo(
    () =>
      pins.map(pin =>
        isSliceTemplate ? (
          <PopupSlice
            {...pin}
            key={pin.id}
            shouldShow={pin.id === selectedPin}
            onClosePopup={onSelectPin}
          />
        ) : (
          <Popup
            {...pin}
            key={pin.id}
            shouldShow={pin.id === selectedPin}
            onClosePopup={onSelectPin}
          />
        )
      ),
    [onSelectPin, pins, selectedPin]
  )

  useEffect(() => {
    setTimeout(() => {
      const buttons = document.querySelectorAll('.mapboxgl-ctrl-attrib-button')
      buttons.forEach(button => {
        button.setAttribute('aria-label', 'Mapbox attribution')
      })
    }, 1000)
  }, [])

  return (
    <ReactMapGl
      aria-hidden
      style={style}
      maxZoom={14}
      initialViewState={initialViewState}
      {...props}
      keyboard={false}
      mapboxAccessToken={REACT_APP_MAPBOX_ACCESS_TOKEN}
      mapStyle="mapbox://styles/kevin-kelly-centivo/cm4n6mi94008b01s5eqob0nuu"
    >
      <div className="controls-container" aria-hidden tabIndex={-1}>
        <NavigationControl showCompass={false} />
      </div>
      {pinArr}
      {popupArr}
    </ReactMapGl>
  )
}
